import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiChevronDown, FiChevronRight, FiUser, FiMail, FiShield, FiAlertCircle, FiTrash2, FiUserMinus } = FiIcons;

const ManageUsersAndTeams = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, usersRes, relationsRes] = await Promise.all([
        apiService.getTeams({ limit: 1000 }),
        apiService.getUsers({ limit: 1000 }),
        apiService.getUserTeamRelations({ limit: 1000 })
      ]);

      setTeams(teamsRes.data || []);
      setUsers(usersRes.data || []);
      setRelations(relationsRes.data || []);
      setError(null);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  };

  const toggleTeam = (teamId) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const getUsersByTeam = (teamId) => {
    const teamRelations = relations.filter(r => r.team_id === teamId);
    const teamUserIds = teamRelations.map(r => r.user_id);
    return users.filter(u => teamUserIds.includes(u.id));
  };

  const getUsersWithoutTeam = () => {
    const userIdsInTeams = relations.map(r => r.user_id);
    return users.filter(u => !userIdsInTeams.includes(u.id));
  };

  const handleRemoveUserFromTeam = async (userId, teamId, e) => {
    e.stopPropagation(); // Prevent toggling accordion
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich aus dem Team entfernen?')) {
      return;
    }

    try {
      const relation = relations.find(r => r.user_id === userId && r.team_id === teamId);
      if (relation) {
        await apiService.deleteUserTeamRelation(relation.id);
        setRelations(prev => prev.filter(r => r.id !== relation.id));
      }
    } catch (err) {
      console.error("Error removing user from team:", err);
      alert("Fehler beim Entfernen des Benutzers.");
    }
  };

  const handleDeleteTeam = async (teamId, e) => {
    e.stopPropagation(); // Prevent toggling accordion
    
    // Double check locally
    const teamUsers = getUsersByTeam(teamId);
    if (teamUsers.length > 0) {
      alert("Das Team kann nicht gelöscht werden, da es noch Mitglieder hat.");
      return;
    }

    if (!window.confirm('Möchten Sie dieses Team wirklich unwiderruflich löschen?')) {
      return;
    }

    try {
      await apiService.deleteTeam(teamId);
      setTeams(prev => prev.filter(t => t.id !== teamId));
    } catch (err) {
      console.error("Error deleting team:", err);
      alert("Fehler beim Löschen des Teams.");
    }
  };

  const usersWithoutTeam = getUsersWithoutTeam();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <SafeIcon icon={FiUsers} className="mr-2" />
          Benutzer & Teams Übersicht
        </h2>
        <div className="text-sm text-gray-500">
          Gesamt: {teams.length} Teams, {users.length} Benutzer
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
          <SafeIcon icon={FiAlertCircle} className="mr-2" />
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Teams List */}
        {teams.map(team => {
          const teamUsers = getUsersByTeam(team.id);
          const isExpanded = expandedTeams.has(team.id);
          const hasMembers = teamUsers.length > 0;

          return (
            <div key={team.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleTeam(team.id)}
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon 
                    icon={isExpanded ? FiChevronDown : FiChevronRight} 
                    className="text-gray-400 w-5 h-5"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{team.name}</h3>
                    <p className="text-sm text-gray-500">Code: {team.code}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {teamUsers.length} Mitglieder
                  </span>
                  <button
                    onClick={(e) => handleDeleteTeam(team.id, e)}
                    disabled={hasMembers}
                    className={`p-1.5 rounded-full transition-colors ${
                      hasMembers 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-red-500 hover:bg-red-50 hover:text-red-700'
                    }`}
                    title={hasMembers ? "Team hat noch Mitglieder" : "Team löschen"}
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 animate-fadeIn">
                  {teamUsers.length > 0 ? (
                    <div className="grid gap-3">
                      {teamUsers.map(user => (
                        <div key={user.id} className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="bg-gray-100 p-2 rounded-full">
                              <SafeIcon icon={FiUser} className="text-gray-500 w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.firstname} {user.lastname}
                              </p>
                              <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                                {user.mail}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {user.is_team_owner === 1 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                Team Owner
                              </span>
                            )}
                            {user.is_admin === 1 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
                                Admin
                              </span>
                            )}
                            <button
                              onClick={(e) => handleRemoveUserFromTeam(user.id, team.id, e)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors ml-2"
                              title="Aus Team entfernen"
                            >
                              <SafeIcon icon={FiUserMinus} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic text-center py-2">Keine Mitglieder in diesem Team.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Users without Team */}
        {usersWithoutTeam.length > 0 && (
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm mt-6">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors bg-gray-50"
              onClick={() => toggleTeam('no-team')}
            >
              <div className="flex items-center space-x-3">
                 <SafeIcon 
                    icon={expandedTeams.has('no-team') ? FiChevronDown : FiChevronRight} 
                    className="text-gray-400 w-5 h-5"
                  />
                <h3 className="font-semibold text-gray-700 italic">Benutzer ohne Team</h3>
              </div>
              <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                {usersWithoutTeam.length} Benutzer
              </span>
            </div>

            {expandedTeams.has('no-team') && (
              <div className="border-t border-gray-200 p-4 animate-fadeIn">
                <div className="grid gap-3">
                  {usersWithoutTeam.map(user => (
                    <div key={user.id} className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between opacity-75">
                      <div className="flex items-center space-x-3">
                        <div className="bg-gray-100 p-2 rounded-full">
                          <SafeIcon icon={FiUser} className="text-gray-400 w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.firstname} {user.lastname}
                          </p>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                            {user.mail}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {user.is_admin === 1 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsersAndTeams;